import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Link as LinkIcon,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  User,
  Phone,
  Mail,
  Building2,
  MapPin,
  Loader2,
  Save,
  Plus,
  Upload,
  ZoomIn,
  ZoomOut,
  UserCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface EmailSignatureEditorProps {
  organizationId: string;
  organizationName: string;
  logoUrl?: string | null;
}

interface Signature {
  id: string;
  signature_name: string;
  signature_html: string;
  signature_text?: string;
  is_default: boolean;
  created_at: string;
}

// Variable placeholders that can be inserted
const VARIABLES = [
  { key: '{{vorname}}', label: 'Vorname', icon: User, description: 'Vorname des Benutzers' },
  { key: '{{nachname}}', label: 'Nachname', icon: User, description: 'Nachname des Benutzers' },
  { key: '{{telefon}}', label: 'Telefon', icon: Phone, description: 'Telefonnummer des Benutzers' },
  { key: '{{email}}', label: 'E-Mail', icon: Mail, description: 'E-Mail-Adresse des Benutzers' },
  { key: '{{adresse}}', label: 'Adresse', icon: MapPin, description: 'Adresse des Benutzers' },
  { key: '{{organisation}}', label: 'Organisation', icon: Building2, description: 'Name der Organisation' },
  { key: '{{logo}}', label: 'Logo', icon: ImageIcon, description: 'Organisations-Logo (mit Größenangabe)' },
  { key: '{{beraterbild}}', label: 'Beraterbild', icon: UserCircle, description: 'Profilbild des Beraters' },
];

export const EmailSignatureEditor = ({ 
  organizationId,
  organizationName,
  logoUrl
}: EmailSignatureEditorProps) => {
  const { user } = useAuth();
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [selectedSignature, setSelectedSignature] = useState<Signature | null>(null);
  const [signatureName, setSignatureName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [logoSize, setLogoSize] = useState(100);
  const [advisorImageSize, setAdvisorImageSize] = useState(80);
  const [customImageSize, setCustomImageSize] = useState(100);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Placeholder.configure({
        placeholder: 'Gestalten Sie hier Ihre E-Mail-Signatur...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[200px] px-4 py-3',
      },
    },
  });

  useEffect(() => {
    loadSignatures();
  }, [organizationId]);

  useEffect(() => {
    if (selectedSignature && editor) {
      editor.commands.setContent(selectedSignature.signature_html);
      setSignatureName(selectedSignature.signature_name);
    }
  }, [selectedSignature, editor]);

  const loadSignatures = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('organization_signatures')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setSignatures(data || []);
      if (data && data.length > 0) {
        setSelectedSignature(data.find(s => s.is_default) || data[0]);
      }
    } catch (error) {
      console.error('Error loading signatures:', error);
      toast.error('Fehler beim Laden der Signaturen');
    } finally {
      setIsLoading(false);
    }
  };

  const insertVariable = useCallback((variable: string) => {
    if (!editor) return;
    
    if (variable === '{{logo}}') {
      if (logoUrl) {
        const fullLogoUrl = logoUrl.startsWith('http') 
          ? logoUrl 
          : supabase.storage.from('organization-logos').getPublicUrl(logoUrl).data.publicUrl;
        
        // Insert logo with size attribute in a special wrapper
        editor.chain().focus().insertContent(
          `<img src="${fullLogoUrl}" alt="${organizationName}" data-type="logo" style="width: ${logoSize}px; height: auto;" />`
        ).run();
      } else {
        // Insert placeholder that will be replaced or removed
        editor.chain().focus().insertContent(`{{logo:${logoSize}}}`).run();
      }
    } else if (variable === '{{beraterbild}}') {
      // Insert placeholder for advisor image with size
      editor.chain().focus().insertContent(`{{beraterbild:${advisorImageSize}}}`).run();
    } else {
      // Insert as text placeholder
      editor.chain().focus().insertContent(variable).run();
    }
  }, [editor, logoUrl, organizationName, logoSize, advisorImageSize]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    setIsUploadingImage(true);
    try {
      // Upload to Supabase storage
      const fileName = `signature-${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('organization-logos')
        .upload(`signatures/${organizationId}/${fileName}`, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('organization-logos')
        .getPublicUrl(data.path);

      // Insert image with size
      editor.chain().focus().insertContent(
        `<img src="${urlData.publicUrl}" alt="Signatur-Bild" style="width: ${customImageSize}px; height: auto;" />`
      ).run();

      toast.success('Bild hochgeladen');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fehler beim Hochladen des Bildes');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const addLink = useCallback(() => {
    if (!linkUrl || !editor) return;
    
    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkUrl('');
  }, [linkUrl, editor]);

  const handleSave = async () => {
    if (!editor || !user || !signatureName.trim()) {
      toast.error('Bitte geben Sie einen Namen für die Signatur ein');
      return;
    }

    setIsSaving(true);
    try {
      const htmlContent = editor.getHTML();
      const textContent = editor.getText();

      if (selectedSignature) {
        // Update existing
        const { error } = await supabase
          .from('organization_signatures')
          .update({
            signature_name: signatureName,
            signature_html: htmlContent,
            signature_text: textContent,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedSignature.id);

        if (error) throw error;
        toast.success('Signatur aktualisiert');
      } else {
        // Create new
        const { error } = await supabase
          .from('organization_signatures')
          .insert({
            organization_id: organizationId,
            created_by: user.id,
            signature_name: signatureName,
            signature_html: htmlContent,
            signature_text: textContent,
            is_default: signatures.length === 0
          });

        if (error) throw error;
        toast.success('Signatur erstellt');
      }

      loadSignatures();
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Fehler beim Speichern der Signatur');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewSignature = () => {
    setSelectedSignature(null);
    setSignatureName('');
    editor?.commands.setContent('');
  };

  const handleSetDefault = async (signatureId: string) => {
    try {
      // Remove default from all
      await supabase
        .from('organization_signatures')
        .update({ is_default: false })
        .eq('organization_id', organizationId);

      // Set new default
      await supabase
        .from('organization_signatures')
        .update({ is_default: true })
        .eq('id', signatureId);

      toast.success('Standard-Signatur festgelegt');
      loadSignatures();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Fehler beim Festlegen der Standard-Signatur');
    }
  };

  const handleDelete = async (signatureId: string) => {
    if (!confirm('Möchten Sie diese Signatur wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('organization_signatures')
        .delete()
        .eq('id', signatureId);

      if (error) throw error;
      
      toast.success('Signatur gelöscht');
      if (selectedSignature?.id === signatureId) {
        handleNewSignature();
      }
      loadSignatures();
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Fehler beim Löschen der Signatur');
    }
  };

  if (!editor) return null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Mail className="h-5 w-5" />
            E-Mail-Signatur
          </CardTitle>
          <CardDescription className="text-sm">
            Erstellen Sie eine einheitliche Signatur für alle Team-Mitglieder. 
            Nutzen Sie Variablen für personalisierte Inhalte. Leere Werte werden automatisch ausgeblendet.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-6">
          {/* Existing Signatures */}
          {signatures.length > 0 && (
            <div className="space-y-3">
              <Label>Vorhandene Signaturen</Label>
              <div className="flex flex-wrap gap-2">
                {signatures.map((sig) => (
                  <div
                    key={sig.id}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors",
                      selectedSignature?.id === sig.id 
                        ? "bg-primary/10 border-primary" 
                        : "hover:bg-muted"
                    )}
                    onClick={() => setSelectedSignature(sig)}
                  >
                    <span className="text-sm font-medium">{sig.signature_name}</span>
                    {sig.is_default && (
                      <Badge variant="secondary" className="text-xs">Standard</Badge>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNewSignature}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Neu
                </Button>
              </div>
            </div>
          )}

          {/* Signature Name */}
          <div className="space-y-2">
            <Label htmlFor="signatureName">Signaturname</Label>
            <Input
              id="signatureName"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="z.B. Standard-Signatur"
            />
          </div>

          {/* Variable Buttons */}
          <div className="space-y-4">
            <Label>Variablen einfügen</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Klicken Sie auf eine Variable, um sie an der aktuellen Cursorposition einzufügen. 
              Die Variablen werden beim Versand automatisch durch die Benutzerdaten ersetzt.
              <strong className="block mt-1">Fehlende Werte werden automatisch ausgeblendet.</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map((variable) => {
                const Icon = variable.icon;
                return (
                  <Button
                    key={variable.key}
                    variant="outline"
                    size="sm"
                    onClick={() => insertVariable(variable.key)}
                    className="gap-1.5 text-xs"
                    title={variable.description}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {variable.label}
                  </Button>
                );
              })}
            </div>

            {/* Image Size Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t">
              {/* Logo Size */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-xs">Logo-Größe: {logoSize}px</Label>
                </div>
                <div className="flex items-center gap-2">
                  <ZoomOut className="h-3 w-3 text-muted-foreground" />
                  <Slider
                    value={[logoSize]}
                    onValueChange={(v) => setLogoSize(v[0])}
                    min={30}
                    max={300}
                    step={10}
                    className="flex-1"
                  />
                  <ZoomIn className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>

              {/* Advisor Image Size */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserCircle className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-xs">Beraterbild: {advisorImageSize}px</Label>
                </div>
                <div className="flex items-center gap-2">
                  <ZoomOut className="h-3 w-3 text-muted-foreground" />
                  <Slider
                    value={[advisorImageSize]}
                    onValueChange={(v) => setAdvisorImageSize(v[0])}
                    min={30}
                    max={200}
                    step={10}
                    className="flex-1"
                  />
                  <ZoomIn className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>

              {/* Custom Image Size */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-xs">Bild-Upload: {customImageSize}px</Label>
                </div>
                <div className="flex items-center gap-2">
                  <ZoomOut className="h-3 w-3 text-muted-foreground" />
                  <Slider
                    value={[customImageSize]}
                    onValueChange={(v) => setCustomImageSize(v[0])}
                    min={30}
                    max={400}
                    step={10}
                    className="flex-1"
                  />
                  <ZoomIn className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>

          {/* Editor */}
          <div className="space-y-2">
            <Label>Signatur-Editor</Label>
            <div className="border rounded-lg bg-background overflow-hidden">
              {/* Toolbar */}
              <div className="border-b bg-muted/30 p-1.5 flex flex-wrap items-center gap-0.5">
                {/* History */}
                <div className="flex items-center">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    icon={Undo}
                    title="Rückgängig"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    icon={Redo}
                    title="Wiederholen"
                  />
                </div>

                <Separator orientation="vertical" className="h-6 mx-1" />

                {/* Text Formatting */}
                <div className="flex items-center">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive('bold')}
                    icon={Bold}
                    title="Fett"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive('italic')}
                    icon={Italic}
                    title="Kursiv"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    active={editor.isActive('underline')}
                    icon={UnderlineIcon}
                    title="Unterstrichen"
                  />
                </div>

                <Separator orientation="vertical" className="h-6 mx-1" />

                {/* Alignment */}
                <div className="flex items-center">
                  <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    active={editor.isActive({ textAlign: 'left' })}
                    icon={AlignLeft}
                    title="Links"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    active={editor.isActive({ textAlign: 'center' })}
                    icon={AlignCenter}
                    title="Zentriert"
                  />
                  <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    active={editor.isActive({ textAlign: 'right' })}
                    icon={AlignRight}
                    title="Rechts"
                  />
                </div>

                <Separator orientation="vertical" className="h-6 mx-1" />

                {/* Link */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn('h-8 w-8 p-0', editor.isActive('link') && 'bg-muted')}
                    >
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-3">
                      <Label>Link URL</Label>
                      <Input
                        placeholder="https://..."
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addLink()}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={addLink} disabled={!linkUrl}>
                          Link einfügen
                        </Button>
                        {editor.isActive('link') && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => editor.chain().focus().unsetLink().run()}
                          >
                            Entfernen
                          </Button>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Separator orientation="vertical" className="h-6 mx-1" />

                {/* Image Upload */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  title="Bild hochladen"
                >
                  {isUploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Editor Content */}
              <EditorContent 
                editor={editor} 
                className="min-h-[200px] bg-white dark:bg-background prose prose-sm max-w-none dark:prose-invert"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Vorschau (mit Beispieldaten)</Label>
            <div className="border rounded-lg p-4 bg-muted/30 min-h-[100px]">
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ 
                  __html: editor.getHTML()
                    .replace(/\{\{vorname\}\}/g, '<span class="text-primary font-medium">Max</span>')
                    .replace(/\{\{nachname\}\}/g, '<span class="text-primary font-medium">Mustermann</span>')
                    .replace(/\{\{telefon\}\}/g, '<span class="text-primary font-medium">+49 123 456789</span>')
                    .replace(/\{\{email\}\}/g, '<span class="text-primary font-medium">max@beispiel.de</span>')
                    .replace(/\{\{adresse\}\}/g, '<span class="text-primary font-medium">Musterstraße 1, 12345 Musterstadt</span>')
                    .replace(/\{\{organisation\}\}/g, `<span class="text-primary font-medium">${organizationName}</span>`)
                    .replace(/\{\{logo:\d+\}\}/g, logoUrl 
                      ? `<img src="${logoUrl.startsWith('http') ? logoUrl : supabase.storage.from('organization-logos').getPublicUrl(logoUrl).data.publicUrl}" alt="Logo" style="max-height: 60px; display: inline-block;" />`
                      : '<span class="text-muted-foreground italic">[Logo nicht verfügbar]</span>')
                    .replace(/\{\{beraterbild:\d+\}\}/g, '<span class="inline-block w-12 h-12 rounded-full bg-primary/20 text-primary text-center leading-[48px] text-xs">Foto</span>')
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={isSaving || !signatureName.trim()}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {selectedSignature ? 'Speichern' : 'Erstellen'}
            </Button>

            {selectedSignature && !selectedSignature.is_default && (
              <Button
                variant="outline"
                onClick={() => handleSetDefault(selectedSignature.id)}
              >
                Als Standard setzen
              </Button>
            )}

            {selectedSignature && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(selectedSignature.id)}
              >
                Löschen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Toolbar Button Component
const ToolbarButton = ({ 
  onClick, 
  disabled, 
  active, 
  icon: Icon, 
  title 
}: { 
  onClick: () => void; 
  disabled?: boolean; 
  active?: boolean; 
  icon: React.ComponentType<{ className?: string }>; 
  title: string;
}) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    className={cn('h-8 w-8 p-0', active && 'bg-muted')}
    title={title}
  >
    <Icon className="h-4 w-4" />
  </Button>
);

export default EmailSignatureEditor;
